import { useQueryClient } from '@tanstack/react-query';
import { Button } from 'primereact/button';
import { useState } from 'react';

import { useOpenS3File } from '@/components/hooks/useOpenS3File';
import { useAttachConditionFileMutation } from '@/components/shared/api/mutations/conditions';
import { Dialog } from '@/components/shared/components/Dialog';
import { FileUploadDialog } from '@/components/shared/components/FileUploadDialog';

import { FileType } from '../../Applicant.types';
import { useApplicantPresignedUpload } from '../ApplicantDocument/useApplicantPresignedUpload';
import {
    AttachedFileBody,
    AttachedFileIcon,
    AttachedFileList,
    AttachedFileMeta,
    AttachedFileName,
    AttachedFileRow,
    AttachedFileTypeChip,
    FormActions,
    ModalContainer,
    ReadonlyConditionTitle,
} from './ApplicantConditions.StyledComponents';
import { Condition } from './ApplicantConditions.types';
import { formatDateTime, getFileTypeLabel } from './ApplicantConditions.utils';

export const useUploadConditionFile = () => {
    const { uploadApplicantDocAws } = useApplicantPresignedUpload();
    const { mutateAsync: attachConditionFile } = useAttachConditionFileMutation();
    const queryClient = useQueryClient();

    return async (
        applicantId: string,
        conditionId: string,
        file: File,
        fileType: FileType | null,
    ) => {
        const uploaded = await uploadApplicantDocAws(applicantId, file, fileType ?? FileType.Other);
        if (!uploaded?.id) {
            throw new Error('Upload did not return a file id.');
        }
        await attachConditionFile({
            applicantId,
            conditionId,
            fileId: uploaded.id,
        });
        queryClient.invalidateQueries({ queryKey: ['applicant-documents', applicantId] });
        // The missing-documents panel counts the ORP's files, so a new upload changes it too.
        queryClient.invalidateQueries({ queryKey: ['applicant-missing-documents', applicantId] });
    };
};

interface Props {
    applicantId: string;
    condition: Condition | null;
    visible: boolean;
    onClose: () => void;
    onFilesAttached?: (condition: Condition, count: number) => void;
    onUploadDialogExited?: () => void;
    canAttach?: boolean;
}

export const AttachedDocumentsDialog = ({
    applicantId,
    condition,
    visible,
    onClose,
    onFilesAttached,
    onUploadDialogExited,
    canAttach = true,
}: Props) => {
    const [showUpload, setShowUpload] = useState(false);
    const uploadConditionFile = useUploadConditionFile();
    const { handleFileClick, getFileDragProps } = useOpenS3File();

    const showList = visible && !showUpload;
    const showUploadDialog = visible && showUpload;

    const handleClose = () => {
        setShowUpload(false);
        onClose();
    };

    const handleUploadFile = async (file: File, fileType: FileType | null) => {
        if (!condition) return;
        await uploadConditionFile(applicantId, condition.id, file, fileType);
    };

    const handleAllUploaded = (totalCount: number) => {
        if (!condition) return;
        onFilesAttached?.(condition, totalCount);
    };

    return (
        <>
            <Dialog
                id="attached-documents-modal"
                header="Attached Documents"
                visible={showList}
                onHide={handleClose}
                dismissableMask={false}
                data-testid="attached-documents-dialog"
            >
                <ModalContainer>
                    <ReadonlyConditionTitle>{condition?.description ?? ''}</ReadonlyConditionTitle>
                    <AttachedFileList>
                        {condition?.files.map((f) => (
                            <AttachedFileRow
                                key={f.id}
                                type="button"
                                onClick={() => handleFileClick(f.id)}
                                {...getFileDragProps(f.id, f.filename)}
                                data-testid={`attached-file-${f.id}`}
                            >
                                <AttachedFileIcon aria-hidden>📄</AttachedFileIcon>
                                <AttachedFileBody>
                                    <AttachedFileName title={f.filename}>
                                        {f.filename}
                                    </AttachedFileName>
                                    <AttachedFileMeta>
                                        Uploaded by {f.uploadedBy ?? 'unknown'} ·{' '}
                                        {formatDateTime(f.uploadDate)}
                                    </AttachedFileMeta>
                                </AttachedFileBody>
                                <AttachedFileTypeChip>
                                    {getFileTypeLabel(f.type)}
                                </AttachedFileTypeChip>
                            </AttachedFileRow>
                        ))}
                    </AttachedFileList>
                    <FormActions style={{ justifyContent: 'flex-end' }}>
                        <Button label="Close" severity="secondary" outlined onClick={handleClose} />
                        {canAttach ? (
                            <Button
                                label="Attach Another"
                                icon="pi pi-upload"
                                severity="info"
                                onClick={() => setShowUpload(true)}
                                data-testid="attach-document-another"
                            />
                        ) : null}
                    </FormActions>
                </ModalContainer>
            </Dialog>

            <FileUploadDialog
                id="attach-condition-file-modal"
                header="Attach Documents to Task/Condition"
                visible={showUploadDialog}
                onClose={handleClose}
                onUploadFile={handleUploadFile}
                onAllUploaded={handleAllUploaded}
                contextLabel={condition?.description ?? ''}
                onExited={onUploadDialogExited}
                data-testid="attach-document-dialog"
            />
        </>
    );
};
